import React, { useState, useCallback, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './ui/button';
import { X, Upload, ZoomIn, ZoomOut, Move } from 'lucide-react';

const AvatarCropModal = ({ isOpen, onClose, onSave, initialImage, imageSrc }) => {
  // Accept both initialImage and imageSrc for backwards compatibility
  const providedImage = imageSrc || initialImage || '';
  
  const [localImgSrc, setLocalImgSrc] = useState('');
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  // Use the provided image if available, otherwise use locally selected image
  const imgSrc = providedImage || localImgSrc;

  const resetState = () => {
    setLocalImgSrc('');
    setCrop(null);
    setCompletedCrop(null);
    setScale(1);
    setImageLoaded(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setLocalImgSrc(reader.result);
        setImageLoaded(false);
        setCrop(null);
        setCompletedCrop(null);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // When image loads, set initial crop that covers the whole image
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    // Calculate a centered square crop
    const size = Math.min(width, height);
    const x = (width - size) / 2;
    const y = (height - size) / 2;
    
    const initialCrop = {
      unit: 'px',
      width: size,
      height: size,
      x: x,
      y: y,
    };
    
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
    setImageLoaded(true);
  }, []);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas to 512x512 for optimal quality
    const pixelRatio = window.devicePixelRatio || 1;
    const outputSize = 512;
    canvas.width = outputSize * pixelRatio;
    canvas.height = outputSize * pixelRatio;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputSize,
      outputSize
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  }, [completedCrop]);

  const handleSave = async () => {
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onSave(croppedBlob);
        resetState();
        onClose();
      }
    } catch (e) {
      console.error('Error cropping image:', e);
    }
  };

  const handleScaleChange = (delta) => {
    setScale(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
      style={{ touchAction: 'none' }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Crop Profile Picture</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition p-1"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {!imgSrc ? (
            <div className="text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onSelectFile}
                className="hidden"
              />
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 sm:p-12 hover:border-[#FFCC00] transition cursor-pointer active:bg-gray-50"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-base sm:text-lg font-semibold text-gray-700 mb-2">Choose an image</p>
                <p className="text-sm text-gray-500">Tap to select a photo from your device</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">
                <Move className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span>Drag the circle to position your photo, then tap Save</span>
              </div>

              {/* Crop Area */}
              <div 
                className="relative bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center"
                style={{ 
                  minHeight: '200px',
                  maxHeight: '50vh',
                  touchAction: 'none'
                }}
              >
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop
                  style={{ maxHeight: '50vh' }}
                >
                  <img
                    ref={imgRef}
                    src={imgSrc}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{
                      transform: `scale(${scale})`,
                      maxHeight: '50vh',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                </ReactCrop>
              </div>

              {/* Zoom Controls */}
              <div className="flex items-center justify-center gap-3 sm:gap-4 py-2">
                <button
                  onClick={() => handleScaleChange(-0.1)}
                  className="p-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition touch-manipulation"
                  title="Zoom out"
                >
                  <ZoomOut className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                </button>
                <div className="flex-1 max-w-[200px] sm:max-w-xs">
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={scale}
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <button
                  onClick={() => handleScaleChange(0.1)}
                  className="p-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition touch-manipulation"
                  title="Zoom in"
                >
                  <ZoomIn className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                </button>
              </div>

              {/* Change Image Button */}
              <div className="text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onSelectFile}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 touch-manipulation"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Different Image
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom, always visible when image is loaded */}
        {imgSrc && (
          <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleClose}
              className="touch-manipulation"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-bold touch-manipulation"
              disabled={!completedCrop || !imageLoaded}
            >
              Save Profile Picture
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarCropModal;
